import React, { useState } from 'react';
import { useEffect, useRef } from 'react';
import Player from '@vimeo/player';
import { CheckCircle } from 'lucide-react';
import { Module } from '../types';

interface CourseModuleProps {
  module: Module;
  onComplete: (moduleId: string, isComplete: boolean) => void;
  isLocked?: boolean;
  onExpand?: (moduleTitle: string) => void;
}

const CourseModule: React.FC<CourseModuleProps> = ({
  module, 
  onComplete,
  isLocked = false,
  onExpand
}) => {
  const [expanded, setExpanded] = useState(false);
  const [hasAutoCompleted, setHasAutoCompleted] = useState(false);
  const [watchProgress, setWatchProgress] = useState(0);
  const [isWatching, setIsWatching] = useState(false);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const playerRef = useRef<Player | null>(null);
  const completedRef = useRef(module.completed);
  const hasAutoCompletedRef = useRef(hasAutoCompleted);
  const onCompleteRef = useRef(onComplete);

  // Keep onCompleteRef current
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Update completedRef when module.completed changes
  useEffect(() => {
    completedRef.current = module.completed;
  }, [module.completed]);

  // Update hasAutoCompletedRef when hasAutoCompleted changes
  useEffect(() => {
    hasAutoCompletedRef.current = hasAutoCompleted;
  }, [hasAutoCompleted]);


  const handleExpand = () => {
    if (!isLocked) {
      setExpanded(!expanded);
      if (!expanded) {
        setHasAutoCompleted(false); // Reset auto-completion flag when expanding
        setWatchProgress(0); // Reset watch progress when expanding
        setIsWatching(false);
      }
      if (onExpand && !expanded) {
        onExpand(module.title);
      }
    }
  };

  // Video progress tracking effect
  useEffect(() => {
    if (expanded && iframeRef.current) {
      // Small delay to ensure iframe is fully loaded
      const timer = setTimeout(() => {
        try {
          const player = new Player(iframeRef.current!);
          playerRef.current = player;

          // Listen for play events
          player.on('play', () => {
            setIsWatching(true);
          });

          // Listen for pause events
          player.on('pause', () => {
            setIsWatching(false);
          });

          // Listen for ended events
          player.on('ended', () => {
            setIsWatching(false);
            if (!completedRef.current && !hasAutoCompletedRef.current) {
              console.log(`Auto-completing module ${module.id} - video ended`);
              setHasAutoCompleted(true);
              onCompleteRef.current(module.id, true);
            }
          });
          // Listen for time updates
          player.on('timeupdate', async (data) => {
            const { seconds, duration } = data;
            const watchedPercentage = (seconds / duration) * 100;
            setWatchProgress(watchedPercentage);

            // Auto-complete when 90% watched and not already completed
            if (watchedPercentage >= 90 && !completedRef.current && !hasAutoCompletedRef.current) {
              console.log(`Auto-completing module ${module.id} at ${watchedPercentage.toFixed(1)}% watched`);
              setHasAutoCompleted(true);
              onCompleteRef.current(module.id, true);
            }
          });

          // Handle player errors gracefully
          player.on('error', (error) => {
            console.warn('Vimeo player error:', error);
          });

        } catch (error) {
          console.warn('Failed to initialize Vimeo player:', error);
        }
      }, 1000);

      return () => {
        clearTimeout(timer);
        if (playerRef.current) {
          try {
            playerRef.current.destroy();
          } catch (error) {
            console.warn('Error destroying Vimeo player:', error);
          }
          playerRef.current = null;
        }
      };
    }
  }, [expanded, module.id]);

  return (
    <div 
      className={`border rounded-lg overflow-hidden mb-4 transition-all duration-300 ${
        module.completed 
          ? 'border-green-300 bg-green-50' 
          : isLocked 
            ? 'border-gray-200 bg-gray-50 opacity-75' 
            : 'border-gray-200 hover:border-blue-200'
      }`}
    >
      <div 
        className={`px-6 py-4 flex justify-between items-center ${
          isLocked ? 'cursor-not-allowed' : 'cursor-pointer'
        }`}
        onClick={handleExpand}
      >
        <div className="flex items-center">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center mr-4 ${
            module.completed 
              ? 'bg-green-500 text-white' 
              : isLocked 
                ? 'bg-gray-300 text-gray-500' 
                : 'bg-blue-600 text-white'
          }`}>
            {module.completed ? (
              <CheckCircle size={20} />
            ) : (
              <span>{module.id.split('-')[0]}</span>
            )}
          </div>
          <div>
            <h3 className={`font-medium ${
              isLocked ? 'text-gray-500' : 'text-gray-800'
            }`}>
              {module.title}
            </h3>
            <p className="text-sm text-gray-500">{module.duration}</p>
          </div>
        </div>
        <div className="flex items-center">
          {isLocked && (
            <span className="text-gray-500 mr-3 text-sm">
              Vorherige Module abschließen
            </span>
          )}
          {expanded && !isLocked && !module.completed && (
            <div className="mr-3 text-sm">
              <div className="flex items-center">
                <div className="w-16 bg-gray-200 rounded-full h-2 mr-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300" 
                    style={{ width: `${Math.min(watchProgress, 100)}%` }}
                  ></div>
                </div>
                <span className="text-gray-600 text-xs">
                  {Math.round(watchProgress)}%
                </span>
              </div>
              {watchProgress >= 90 && (
                <span className="text-green-600 text-xs">
                  ✓ Abgeschlossen
                </span>
              )}
            </div>
          )}
          <svg 
            className={`w-5 h-5 text-gray-500 transform transition-transform ${
              expanded ? 'rotate-180' : ''
            }`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
          </svg>
        </div>
      </div>
      
      {expanded && !isLocked && (
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="aspect-w-16 aspect-h-9 mb-4">
            <iframe 
              ref={iframeRef}
              src={module.videoUrl}
              className="w-full h-[300px]"
              frameBorder="0" 
              allow="autoplay; fullscreen; picture-in-picture" 
              allowFullScreen
              title={module.title}
            ></iframe>
          </div>
          
          <div className="mt-4">
            {module.completed ? (
              <div className="flex items-center text-green-600">
                <CheckCircle className="w-5 h-5 mr-2" />
                <span className="font-medium">
                  Modul abgeschlossen
                  {hasAutoCompleted && (
                    <span className="ml-2 text-sm">(automatisch bei 90% abgeschlossen)</span>
                  )}
                </span>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-blue-800 font-medium">Video-Fortschritt</span>
                  <span className="text-blue-600 text-sm">
                    {Math.round(watchProgress)}% von 90% erforderlich
                  </span>
                </div>
                <div className="w-full bg-blue-200 rounded-full h-3">
                  <div 
                    className="bg-blue-600 h-3 rounded-full transition-all duration-300" 
                    style={{ width: `${Math.min(watchProgress, 100)}%` }}
                  ></div>
                </div>
                <p className="text-blue-700 text-sm mt-2">
                  {watchProgress < 90 
                    ? `Schauen Sie das Video zu mindestens 90% an, um das Modul abzuschließen.`
                    : `✓ Video vollständig angeschaut! Modul wird automatisch abgeschlossen.`
                  }
                </p>
                {isWatching && (
                  <div className="flex items-center mt-2 text-blue-600">
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-pulse mr-2"></div>
                    <span className="text-sm">Video läuft...</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseModule;